import React, { useEffect, useRef, useState } from 'react';
import { BioScoreResult, WorkflowProps } from '@/types/workflow';
import {
	GeometryDataItem,
	getSelectedBuildingFunctions,
	getSelectedPaths,
	parsePath,
} from '@/services/forma/forma-helpers';
import { SpacePlanning } from '@/components/SpacePlanningPanel/SpacePlanning';
import styles from './styles.module.scss';
import { Icon } from '@/components/UI/Icon/Icon';
import { WeaveTooltip } from '@/components/UI/WeaveTooltip/WeaveTooltip';
import { WeaveIconButton } from '@/components/UI/WeaveIconButton/WeaveIconButton';
import { WeaveBanner } from '@/components/UI/WeaveBanner/WeaveBanner';
import { WeaveButton } from '@/components/UI/WeaveButton/WeaveButton';
import { WeaveAccordion } from '@/components/UI/WeaveAccordion/WeaveAccordion';
import { SpaceData } from '@/components/SpacePlanningPanel/SpacePlanningTypes';
import { useSpacePlanningStore } from '@/store/spacePlanningStore';
import SkeletonLoader from '@/components/UI/SkeletonLoader/SkeletonLoader';
import { useWorkflowStore } from '@/store/workflowStore';
import {
	applyFunctionRenameMap,
	buildFunctionRenameMap,
	pickPreferredSpaceData,
	resolveLegacySection2Key,
} from '@/services/workflow/functionKeySync';
import { newModal, WeaveModalElement } from '@/components/UI/WeaveModal/WeaveModal';

type SpaceConfigurationProps = WorkflowProps & {
	objectLockEnabled?: boolean;
	onActiveObjectChange?: (payload: {
		contextKey: string;
		selectedPath: string;
		functionSummary: string;
	}) => void;
};

const SpaceConfiguration: React.FC<SpaceConfigurationProps> = ({
	onDataChange,
	workflowData,
	activeSection,
	currentStage,
	objectLockEnabled = true,
	onActiveObjectChange,
}) => {
	const { spaces, renameSpaceKey } = useSpacePlanningStore();
	const renameFunctionKeys = useWorkflowStore((state) => state.renameFunctionKeys);
	const renamePaletteAssignmentKeys = useWorkflowStore(
		(state) => state.renamePaletteAssignmentKeys
	);

	const [isLoading, setIsLoading] = useState(false);
	const [data, setData] = useState<GeometryDataItem[]>([]);
	const [selectedPath, setSelectedPath] = useState<string | null>(null);
	const lastSection2SnapshotRef = useRef<string>('');
	const [bioScoreResult, setBioScoreResult] = useState<BioScoreResult>({
		data: workflowData[activeSection]?.data ?? null,
	});

	const closeConfirmModal = () => {
		const existingModal = document.querySelector('weave-modal') as WeaveModalElement | null;
		if (!existingModal) return;
		existingModal.close();
		existingModal.remove();
	};

	const askObjectSwitchConfirmation = (
		currentObject: string,
		nextObject: string,
		currentFunctions: string,
		nextFunctions: string
	) =>
		new Promise<boolean>((resolve) => {
			let settled = false;

			const settle = (value: boolean) => {
				if (settled) return;
				settled = true;
				resolve(value);
			};

			const handleCancel = () => {
				closeConfirmModal();
				settle(false);
			};

			const handleConfirm = () => {
				closeConfirmModal();
				settle(true);
			};

			newModal({
				title: 'Switch active object?',
				width: '540px',
				content: (
					<div className={styles.objectSwitchModalContent}>
						<p className={styles.objectSwitchModalText}>
							You are currently working on <b>{currentObject}</b>. Do you want to
							switch to <b>{nextObject}</b>?
						</p>
						<div className={styles.objectSwitchModalMeta}>
							<div>
								<b>Current functions:</b> {currentFunctions}
							</div>
							<div>
								<b>Selected object functions:</b> {nextFunctions}
							</div>
						</div>
						<div className={styles.objectSwitchModalActions}>
							<WeaveButton variant="outlined" onClick={handleCancel}>
								Stay on current object
							</WeaveButton>
							<WeaveButton variant="solid" onClick={handleConfirm}>
								Switch object
							</WeaveButton>
						</div>
					</div>
				),
			});

			const modal = document.querySelector('weave-modal') as WeaveModalElement | null;
			modal?.addEventListener(
				'close',
				() => {
					settle(false);
				},
				{ once: true }
			);
		});

	const getSelectionContextKey = (path: string) => {
		const { building } = parsePath(path);
		const pathParts = building.split('/');
		const objectToken = pathParts[1];
		if (!objectToken) return building;

		const shortIdMatch = objectToken.match(/^([a-f0-9]{7})(-.+)?$/i);
		if (shortIdMatch) {
			return `root/${shortIdMatch[1]}`;
		}

		return building;
	};

	const getFunctionSummary = (formaData: GeometryDataItem[]) => {
		const functionNames = Array.from(
			new Set(
				formaData.flatMap((item) =>
					item.functions
						.map((fn) => fn.functionName?.trim())
						.filter((name): name is string => Boolean(name))
				)
			)
		);

		if (functionNames.length === 0) return 'No functions';
		if (functionNames.length <= 2) return functionNames.join(', ');
		return `${functionNames.slice(0, 2).join(', ')} +${functionNames.length - 2}`;
	};

	const syncRenamedFunctionKeys = (building: string, formaData: GeometryDataItem[]) => {
		const liveSpaces = useSpacePlanningStore.getState().spaces;
		const liveWorkflowData = useWorkflowStore.getState().workflowData;
		const section2Data =
			(liveWorkflowData[2]?.data as Record<string, unknown> | undefined) || {};
		const section4Data =
			(liveWorkflowData[4]?.data as Record<string, unknown> | undefined) || {};
		const sourceKeys = Array.from(
			new Set([
				...Object.keys(liveSpaces),
				...Object.keys(section2Data),
				...Object.keys(section4Data),
			])
		);
		const renameMap = buildFunctionRenameMap(building, formaData, sourceKeys);

		applyFunctionRenameMap({
			renameMap,
			spaceKeys: new Set(Object.keys(liveSpaces)),
			renameSpaceKey,
			renameFunctionKeys,
			renamePaletteAssignmentKeys,
		});
	};

	const getFormaData = async () => {
		const paths = await getSelectedPaths();

		if (paths.length !== 1) {
			setSelectedPath(null);
			setData([]);
			setBioScoreResult({ data: null });
			return;
		}

		const nextPath = paths[0];
		const currentContextKey = selectedPath ? getSelectionContextKey(selectedPath) : null;
		const nextContextKey = getSelectionContextKey(nextPath);
		let result: GeometryDataItem[] | null = null;

		if (objectLockEnabled && currentContextKey && currentContextKey !== nextContextKey) {
			result = await getSelectedBuildingFunctions();
			const currentFunctions = getFunctionSummary(data);
			const nextFunctions = getFunctionSummary(result);
			const shouldContinue = await askObjectSwitchConfirmation(
				currentContextKey,
				nextContextKey,
				currentFunctions,
				nextFunctions
			);
			if (!shouldContinue) {
				return;
			}
		}

		setSelectedPath(nextPath);
		setIsLoading(true);

		const finalResult = result ?? (await getSelectedBuildingFunctions());
		const { building } = parsePath(nextPath);
		syncRenamedFunctionKeys(building, finalResult);
		setData(finalResult);
		onActiveObjectChange?.({
			contextKey: nextContextKey,
			selectedPath: nextPath,
			functionSummary: getFunctionSummary(finalResult),
		});

		setIsLoading(false);
	};

	const handleRefreshData = (event?: unknown) => {
		const target = event as { currentTarget?: HTMLElement };
		target.currentTarget?.blur();
		(document.activeElement as HTMLElement | null)?.blur();
		getFormaData();
	};

	useEffect(() => {
		getFormaData();
	}, []);

	useEffect(() => {
		if (!selectedPath || !data.length) return;

		const { building } = parsePath(selectedPath);
		const allSpaceData: Record<string, SpaceData> = {};
		const liveSpaces = useSpacePlanningStore.getState().spaces;
		const liveWorkflowData = useWorkflowStore.getState().workflowData;
		const section2Data =
			(liveWorkflowData[2]?.data as Record<string, SpaceData> | undefined) || {};

		data.forEach((fn) => {
			fn.functions.forEach((s) => {
					const key = `${building}-${s.functionId}`;
					const legacySection2Key = resolveLegacySection2Key(key, building, section2Data);
					const liveSpace = liveSpaces[key];
					const fallbackSpace = pickPreferredSpaceData(
						section2Data[key],
						legacySection2Key ? section2Data[legacySection2Key] : undefined
					);
					const space =
						liveSpace ?? fallbackSpace ??
						({ tare: 0.15, rooms: [], name: '', score: 0 } as SpaceData);

				allSpaceData[key] = {
					name: s.functionName || '',
					tare: space.tare,
					score: space.score || 0,
					rooms: space.rooms || [],
				};
			});
		});

		const nextSnapshot = JSON.stringify(allSpaceData);
		if (nextSnapshot !== lastSection2SnapshotRef.current) {
			lastSection2SnapshotRef.current = nextSnapshot;
			setBioScoreResult((prev) => ({
				...prev,
				data: allSpaceData,
			}));
		}
	}, [selectedPath, data, spaces]);

	useEffect(() => {
		onDataChange({
			data: bioScoreResult.data,
		});
	}, [bioScoreResult, onDataChange]);

	if (isLoading) {
		return <SkeletonLoader title="Loading space planning data..." loading={isLoading} />;
	}

	return (
		<>
			<div className={styles.instructions}>
				<div className={styles.instructionsHeader}>
					<h2 className={styles.instructionsTitle}>Configure Spaces and Rooms</h2>
					<div className={styles.instructionsActions}>
						<div className={styles.refreshAction}>
							<WeaveTooltip
								text="Fetch data"
								nub="right-center"
								opendelay={1000}
								width="300px"
								description="Click this button to refresh and retrieve the latest data whenever changes are made to an object or floor."
							>
								<WeaveIconButton onClick={handleRefreshData}>
									<Icon
										name="forma-refresh-16"
										className={styles.instructionsIcon}
									/>
								</WeaveIconButton>
							</WeaveTooltip>
						</div>
					</div>
				</div>
				<p className={styles.instructionsText}>
					This tab is the setup baseline: define functions, reserve tare, and prepare room
					data before running analyses.
				</p>
				<div className={styles.guidanceAccordion}>
					<WeaveAccordion label="Functions and Spaces Guidance" indicatorposition="right">
						<div className={styles.guidanceContent}>
							<p className={styles.guidanceHeading}>Why this tab matters</p>
							<ul className={styles.instructionsList}>
								<li>
									This tab defines the base configuration used by all other
									analysis and scoring tabs.
								</li>
								<li>
									If data is missing elsewhere, return here, select a building or
									floor, and click <b>Refresh</b> to reload the project state.
								</li>
							</ul>
							<p className={styles.guidanceHeading}>Initial setup</p>
							<ul className={styles.instructionsList}>
								<li>
									Define a site limit and create at least one building in Forma.
								</li>
								<li>
									Select exactly one building or floor, then click <b>Refresh</b>{' '}
									to load data into the extension.
								</li>
								<li>
									After any change in Forma, click <b>Refresh</b> to sync updates.
								</li>
								<li>
									Use <b>Object Lock</b> in the footer to control object
									switching. When lock is on, switching to another object requires
									confirmation.
								</li>
							</ul>
							<p className={styles.guidanceHeading}>Functions and area</p>
							<ul className={styles.instructionsList}>
								<li>
									Create functions in Forma and assign them to the building or a
									specific floor, then click <b>Refresh</b>.
								</li>
								<li>
									Use tare to reserve a portion of unassigned area within the
									selected building or floor.
								</li>
							</ul>
							<p className={styles.guidanceHeading}>By project stage</p>
							<ul className={styles.instructionsList}>
								<li>
									<b>Predesign / Schematic Design:</b> configure at the function
									level only (rooms are not available).
								</li>
								<li>
									<b>Design Development:</b> add rooms and define room-level
									inputs such as materials, palettes, and environmental options.
								</li>
								<li>
									Palettes can still be assigned at the building level in earlier
									stages via the Materials tab.
								</li>
							</ul>
						</div>
					</WeaveAccordion>
				</div>

				{!selectedPath && (
					<div style={{ marginTop: '20px' }}>
						<WeaveBanner
							variant="warning"
							text="Select exactly one building or floor (within a defined site limit), then click refresh."
						/>
					</div>
				)}
			</div>
			<div>
				<SpacePlanning formaData={data} currentStage={currentStage} />
			</div>
		</>
	);
};

export default SpaceConfiguration;
